import axios from 'axios';
import { supabase } from '../utils/supabaseClient';

const API_BASE = '/api/invitations';

/**
 * Free text, not a union.
 *
 * Roles are rows in org_role_permissions, created per organisation by an admin, so the set is not
 * knowable at compile time. Both base_organisation_invitations.role and
 * base_organisation_members.role are plain text columns with no check constraint, and the invite
 * routes copy the value straight through, so a custom role survives invite -> accept -> membership.
 *
 * The old union also listed 'viewer', which was never a real role: nothing grants it permissions,
 * so anyone invited as Viewer landed with no access at all.
 */
export type InvitationRole = string;
export type InvitationStatus = 'pending' | 'accepted' | 'rejected' | 'revoked';

export type Invitation = {
    id: string;
    organisation_id: string;
    invited_by: string;
    email: string;
    role: InvitationRole;
    status: InvitationStatus;
    invited_at: string;
    accepted_at: string | null;
    created_at: string;
};

export type InviteCheckResult =
    | { hasInvite: false }
    | { hasInvite: true; invitationId: string; orgId: string; orgName: string; role: string };

const getAxios = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return axios.create({
        baseURL: API_BASE,
        headers: { 'x-user-id': session?.user?.id || '', 'Content-Type': 'application/json' },
    });
};

export const invitationService = {
    sendInvite: async (orgId: string, email: string, role: InvitationRole = 'member') => {
        const api = await getAxios();
        const res = await api.post('/send', { orgId, email, role });
        return res.data as { success: boolean; invitation: Invitation; signupLink: string };
    },

    listInvitations: async (orgId: string, page = 1, pageSize = 20) => {
        const api = await getAxios();
        const res = await api.get('/list', { params: { orgId, page, pageSize } });
        return res.data as { invitations: Invitation[]; total: number; page: number; pageSize: number };
    },

    revokeInvitation: async (id: string) => {
        const api = await getAxios();
        const res = await api.patch(`/${id}/revoke`);
        return res.data as { success: boolean };
    },

    /** Check if an email has a pending invite — used on signup/onboarding */
    checkEmail: async (email: string): Promise<InviteCheckResult> => {
        const res = await axios.post(`${API_BASE}/check-email`, { email });
        return res.data;
    },

    acceptInvitation: async (invitationId: string) => {
        const api = await getAxios();
        const res = await api.post('/accept', { invitationId });
        return res.data as { success: boolean; role: string; orgId: string };
    },
};
